// components/landing/HeroSection.js
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  FiArrowRight,
  FiBell,
  FiTrendingUp,
  FiRepeat,
  FiPower,
} from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';


const HeroSection = () => {
  const features = [
    {
      title: "Notificações Push",
      description: "10x mais eficazes que o e-mail marketing. Alcance imediato.",
      Icon: FiBell,
    },
    {
      title: "Conversão Instantânea",
      description: "Até 30% mais taxas de conversão com checkout nativo.",
      Icon: FiTrendingUp,
    },
    {
      title: "Receita Recorrente",
      description: "Clientes retornam 3x mais no aplicativo móvel.",
      Icon: FiRepeat,
    },
    {
      title: "Publicação Rápida",
      description: "Aplicativo publicado nas lojas em até 24 horas.",
      Icon: FiPower,
    }
  ];

  // Animated app category icons
  const appCategories = [
    { icon: '📱', label: 'mobile' },
    { icon: '🛒', label: 'loja virtual' },
    { icon: '🍕', label: 'delivery' },
    { icon: '💊', label: 'farmácia' },
    { icon: '🐾', label: 'pet shop' },
    { icon: '💈', label: 'barbearia' },
    { icon: '🏋️', label: 'academia' },
    { icon: '🌿', label: 'saúde' },
  ];

  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentCategoryIndex(prev => (prev + 1) % appCategories.length);
        setIsVisible(true);
      }, 300);
    }, 2200);
    return () => clearInterval(interval);
  }, [appCategories.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <section 
      id="hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-white"
      style={{ 
        paddingTop: 'calc(var(--header-height, 140px) + var(--hero-pt, 1.5rem))',
        paddingBottom: 'var(--hero-pb, 3.5rem)'
      }}
    >
      {/* Background Pattern - Subtle diagonal stripes for visual depth without cliché gradients */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)`,
          backgroundSize: '10px 10px'
        }}
      ></div>

      <div className="container mx-auto px-4 relative z-10 max-w-7xl w-full">
        <motion.div 
          className="max-w-4xl mx-auto text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Top Badge */}
          <motion.div variants={itemVariants} className="mb-6 flex justify-center px-4">
            <div className="inline-flex flex-wrap justify-center items-center gap-2 px-4 py-2 rounded-full border border-gray-100 bg-gray-50/50 backdrop-blur-sm text-xs sm:text-sm font-medium text-gray-800 text-center">
              <span className="font-bold">v2.0 Release:</span>
              <span className="text-gray-600">Crie seu app nativo 10x mais rápido</span>
            </div>
          </motion.div>

          {/* Main Title - Massive Typographic Style */}
          <motion.h1 
            variants={itemVariants}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight mb-6 px-2 flex flex-wrap justify-center items-center gap-x-2 sm:gap-x-3 text-center w-full"
          >
            <span>Vá além da Web. Lance seu App</span>
            <span
              style={{
                display: 'inline-flex',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.85)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                fontSize: '0.85em',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              className="text-indigo-600"
              aria-label={appCategories[currentCategoryIndex].label}
            >
              {appCategories[currentCategoryIndex].icon}
            </span>
            <span>e multiplique suas vendas</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            variants={itemVariants}
            className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed font-light px-4 text-center"
          >
            No mundo todo há mais de <strong className="font-semibold text-gray-800">7,5 bilhões</strong> de usuários de celular. Acesse o público com maior poder aquisitivo e dispare suas vendas marcando presença na Apple Store e Google Play.
          </motion.p>

          {/* Action Buttons */}
          <motion.div 
            variants={itemVariants}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 px-4"
          >
            <Link to="/login" className="w-full sm:w-auto block">
              <button 
                className="w-full sm:w-auto px-6 py-2.5 bg-[#0a0a0a] text-white rounded-lg font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2 hover:bg-[#1a1a1a] hover:shadow-[inset_0_0_10px_rgba(255,255,255,0.1)] outline-none cursor-pointer"
              >
                Criar meu App <FiArrowRight />
              </button>
            </Link>
            
            <a 
              href="#features"
              className="w-full sm:w-auto px-6 py-2.5 bg-white text-gray-900 border border-gray-200 rounded-lg font-medium text-sm transition-all duration-300 flex items-center justify-center hover:bg-gray-50 hover:shadow-[inset_0_0_10px_rgba(0,0,0,0.03)] outline-none cursor-pointer text-center"
            >
              Explore as funcionalidades
            </a>
          </motion.div>

          {/* Tech/Partners Logos */}
          <motion.div 
            variants={itemVariants}
            className="flex flex-wrap justify-center items-center gap-8 text-gray-400 mb-12"
          >
            <a href="https://www.apple.com/app-store/" target="_blank" rel="noopener noreferrer" title="App Store" className="outline-none">
              <FaApple className="w-[32px] h-[32px] hover:text-gray-800 transition-colors cursor-pointer relative -top-[2px]" />
            </a>
            <a href="https://play.google.com/store/apps" target="_blank" rel="noopener noreferrer" title="Google Play" className="outline-none">
              <FaGooglePlay className="w-7 h-7 hover:text-gray-800 transition-colors cursor-pointer" />
            </a>
          </motion.div>
        </motion.div>

        {/* Feature Cards Grid (4 columns) */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
              className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-gray-200 transition-all text-left flex flex-col items-start cursor-default"
            >
              <div className="mb-5 text-gray-800 bg-gray-50 p-3 rounded-xl border border-gray-100">
                <feature.Icon className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-2" translate="no">{feature.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed font-light">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default HeroSection;